import React from "react";
import { motion, AnimatePresence } from "motion/react";
import { Alert, CurrencyInput } from "@picklebet/ui";
import { cx } from "class-variance-authority";
import { isDepositLimitReachedMessage } from "utilities/Wallet/walletUtilities";
import { useProfile } from "hooks/firestore/user/useProfile";
import { useMaxDepositAmount } from "features/wallet/hooks/useMaxDepositAmount";
import { Link } from "@/features/shell/components/shared/Link/Link";
import { AmountPresets } from "@/features/wallet/components/shared/amount-presets/AmountPresets";
import { useDepositWizard } from "features/wallet/components/deposit/hooks/useDepositWizard";
import { DepositBonusBadge } from "../deposit-bonus-badge/DepositBonusBadge";
import * as styles from "./DepositAmountInput.module.scss";

const DEPOSIT_INCREMENTS = [10, 25, 50, 100] as const;

type DepositAmountInputProps = {
  value: number;
  onChange: (next: number) => void;
  errorMessage?: string;
  /** per-transaction cap in cents, where it is lower than the daily limit */
  maxAmountOverrideCents?: number;
  disabled?: boolean;
};

export const DepositAmountInput = ({
  value,
  onChange,
  errorMessage,
  maxAmountOverrideCents,
  disabled = false,
}: DepositAmountInputProps) => {
  const { profile } = useProfile();
  const currency = profile?.fiatCurrency || "AUD";
  const isLimitReached = isDepositLimitReachedMessage(errorMessage);
  const { maxDepositAmount } = useMaxDepositAmount(maxAmountOverrideCents);
  const { requiredAmount } = useDepositWizard();

  return (
    <>
      {isLimitReached && (
        <Alert
          title="You have hit your deposit limit"
          color="danger"
          size="small"
        >
          <p>
            {errorMessage}.{" "}
            <Link to="/preferences/responsible-gambling/">
              Adjust your limits
            </Link>
          </p>
        </Alert>
      )}

      <div className={styles.depositAmountContainer}>
        <CurrencyInput
          inputClassName={cx(
            styles.currencyInput,
            value.toString().length >= 8 && styles.textMd,
          )}
          onChange={(next) => onChange(next)}
          currency={currency}
          canClear={false}
          wrapperClassName={cx(
            styles.currencyInputWrapper,
            disabled && styles.disabled,
          )}
          disableFocusStyling
          noSpacing
          disabled={disabled}
          value={value}
        />
        <AnimatePresence initial={false}>
          {errorMessage && !isLimitReached && (
            <motion.div
              key={errorMessage}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
            >
              <span className={styles.errorMessage}>{errorMessage}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <DepositBonusBadge amount={value} />
      </div>

      <AmountPresets
        currency={currency}
        value={value}
        onSelect={onChange}
        increments={DEPOSIT_INCREMENTS}
        maxAmountInCents={maxDepositAmount}
        requiredAmount={requiredAmount}
        disabled={disabled}
      />
    </>
  );
};
